<template>
  <div
    v-if="!hideInitially || revealed"
    class="error-message d-flex justify-center"
    :class="{ 'error-message--inline': inline }"
    :data-test="dataTest"
  >
    <div ref="topMarker" />
    <div
      ref="container"
      :key="triggerCount"
      :class="{ shake: triggerCount }"
      class="error-message__container animated fast"
    >
      <headline-h3
        v-if="title"
        headline-class="error-headline ma-0"
        headline-seo
        :headline="title"
      />
      <div class="error-message__text">
        <slot />
      </div>
      <div
        v-if="code"
        class="error-message__code pt-3"
      >
        {{ $dict.get('online-Shop.Global-Labels.Error-Code') }}
        {{ code }}
      </div>
    </div>
    <div ref="bottomMarker" />
  </div>
</template>

<script setup lang="ts">
// HeadlineH3 fails because of "Dependency cycle detected (import/no-cycle)"
// Solution might be: https://medium.com/visual-development/how-to-fix-nasty-circular-dependency-issues-once-and-for-all-in-javascript-typescript-a04c987cf0de
import { nextTick, onMounted, ref, useTemplateRef } from 'vue';

import { scrollToElement } from '@/utils/pageScrolling';

import HeadlineH3 from './headline-h3/HeadlineH3.vue';

const props = defineProps<{
  title?: string | null;
  inline?: boolean;
  code?: string | null;
  hideInitially?: boolean;
  dataTest?: string | null;
  triggerOnCreation?: boolean;
  scrollTo?: string;
}>();

const revealed = ref(false);
const triggerCount = ref(0);

const topMarker = useTemplateRef('topMarker');
const container = useTemplateRef('container');
const bottomMarker = useTemplateRef('bottomMarker');

async function trigger() {
  // :key="triggerCount" will re-insert the element in the dom when the
  // message gets triggered a second time. This is more reliable than
  // removing and re-adding the shake class.
  triggerCount.value += 1;
  revealed.value = true;
  if (IS_SSR) {
    return;
  }
  await nextTick();
  if (props.scrollTo) {
    scrollToElement(document.querySelector(`#${props.scrollTo}`));
  } else if (container.value) {
    const boundingClientRect = container.value.getBoundingClientRect();
    if (boundingClientRect.y <= 200) {
      scrollToElement(topMarker.value);
    } else if (boundingClientRect.bottom > document.documentElement.clientHeight - 200) {
      scrollToElement(bottomMarker.value);
    }
  }
}

function hide() {
  revealed.value = false;
}

onMounted(() => {
  if (props.triggerOnCreation) {
    trigger();
  }
});

defineExpose({ trigger, hide });
</script>

<style scoped src="./error-messages" />
